import CLIP_DATA from '../data/player-clips.json';
import { CLIP_LOOK } from '../data/clipLook';
import { normName } from './playerNameNorm';

/**
 * playerClips.js — name in, Giphy mp4 URL out. Or null, which is the answer for
 * almost everybody.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 NULL IS THE DEFAULT RETURN AND THE DEFAULT PATH. Two players are in
 * player-clips.json; roughly three hundred are not. Every caller has to render
 * correctly for null FIRST — the clip is the exception, not the feature.
 *
 * 🔴 THIS FILE IS HAND-EDITED AND ITS CONTENTS BECOME A `src` ATTRIBUTE AND A
 * `style` ATTRIBUTE. So both fields are validated, not trusted: the id is
 * letters and digits only, nothing that could carry a path segment, a query, or
 * an absolute URL out of the media host; `focus` is a keyword-or-percentage
 * allowlist, nothing that could close a declaration and open another. A bad
 * value is DROPPED at load — a bad id means the player simply has no clip,
 * which is the shipped card; a bad focus means the shipped framing.
 *
 * ⚠️ A well-formed id that does not exist still 404s. That is not this module's
 * problem to solve and it must not try: the video element's own onError is the
 * backstop, and it lands on the same card as an empty file.
 * ══════════════════════════════════════════════════════════════════════
 *
 * The index is built ONCE at module load, keyed by normName, so a lookup during
 * a live auction is a Map hit and nothing else.
 */
export const GIPHY_URL_PREFIX = 'https://media.giphy.com/media/';

// Letters and digits, nothing else. Deliberately not a loose "no slashes"
// check: an allowlist cannot be talked around by an encoding trick.
const GIPHY_ID = /^[A-Za-z0-9]{6,40}$/;

// One `object-position` component: a keyword or a percentage. Narrow on
// purpose — the useful cases are "center 20%" and "left top", and the same
// allowlist-not-denylist reasoning as the id applies, because this string is
// handed to a style attribute.
const FOCUS_PART = /^(left|right|top|bottom|center|-?\d{1,3}(\.\d+)?%)$/;

/**
 * A safe `object-position` value, or null.
 *
 * Null means "emit nothing". Note that null is no longer what an ABSENT field
 * produces — see `clipFor` — because the shared default moved off `center`.
 * Null is now reserved for a value the allowlist refused.
 */
export function safeFocus(raw) {
  if (typeof raw !== 'string') return null;
  const parts = raw.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (parts.length < 1 || parts.length > 2) return null;   // 3- and 4-value forms not supported
  if (!parts.every(p => FOCUS_PART.test(p))) return null;
  return parts.join(' ');
}

/**
 * Build the name -> clip index from raw file contents.
 *
 * Accepts BOTH the object form this repo's data files use ({ clips: [...] })
 * and a bare array, because that is the shape a person writes by hand when they
 * add an entry in a hurry. Anything else — null, a string, an object with no
 * clips key — is an empty index rather than a throw: a decoration file must
 * never be able to take the board down on draft night.
 *
 * Exported for tests. Nothing else should call it.
 */
export function buildClipIndex(raw) {
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.clips) ? raw.clips : []);
  const index = new Map();
  for (const entry of list) {
    const key = normName(entry?.player);
    const id = typeof entry?.giphyId === 'string' ? entry.giphyId.trim() : '';
    if (!key || !GIPHY_ID.test(id)) continue;
    // A bad focus does NOT cost the player his clip — it costs him the framing.
    // The id is the entry; the focus is a tweak on it.
    if (!index.has(key)) index.set(key, { id, focus: safeFocus(entry?.focus) });
  }
  return index;
}

const INDEX = buildClipIndex(CLIP_DATA);

/**
 * The crop applied to any clip that did not ask for one.
 *
 * 🔴 RUN THROUGH THE SAME ALLOWLIST AS THE DATA FILE. It is a hand-edited
 * constant that lands in a style attribute on every one of ~45 clips, so a typo
 * during a tuning pass would otherwise reach the DOM on every card at once —
 * a wider blast radius than any single entry has. A refused default falls back
 * to emitting nothing, which is the CSS default and the shipped framing.
 */
const DEFAULT_FOCUS = safeFocus(CLIP_LOOK.defaultFocus);

/**
 * This player's clip, or null. Null for anyone not listed, which is almost
 * everyone.
 *
 * `{ url, focus }` — `focus` is the entry's own if it has one, otherwise the
 * shared default. ⚠️ It is therefore normally NON-null now: highlight clips
 * frame a whole body and the interesting part is the top third, so centring is
 * the wrong default and the right one has to be written out to take effect.
 */
export function clipFor(name) {
  const hit = INDEX.get(normName(name));
  if (!hit) return null;
  return {
    url: `${GIPHY_URL_PREFIX}${hit.id}/giphy.mp4`,
    focus: hit.focus || DEFAULT_FOCUS,
  };
}
